import React, { useEffect, useRef, useState } from 'react';
import { Upload, Scan, Camera, X } from "lucide-react";
import {
  FaFileAlt,
  FaTimes,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import processPrescriptionFile from "../Aireports/processPrescriptionImage";

export default function Scanner() {
  const [activeTab, setActiveTab] = useState("upload");
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [files, setFiles] = useState([]);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);



  // 👇 Automatically scan after all uploads finish
  useEffect(() => {
    const allUploadsComplete =
      files.length > 0 &&
      files.every((file) => file.status === "completed");

    if (allUploadsComplete && activeTab === "upload" && !loading && !result) {
      handleScanDocument();
    }
  }, [files]);


  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraActive(true);
      }
    } catch (err) {
      alert("Unable to access camera.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageDataURL = canvas.toDataURL("image/png");
      setCapturedImage(imageDataURL);
      stopCamera();
    }
  };

  const resetCapture = () => {
    setCapturedImage(null);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "camera") startCamera();
    else stopCamera();
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    resetState();
    processFiles(selectedFiles);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    resetState();
    processFiles(droppedFiles);
    setIsHovering(false);
  };

  const resetState = () => {
    setFiles([]);
    setResult("");
  };

  const processFiles = (selectedFiles) => {
    const updatedFiles = selectedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      progress: 0,
      status: file.size > 5 * 1024 * 1024 ? "error" : "uploading",
      file,
    }));
    setFiles(updatedFiles);
    updatedFiles.forEach((file, i) => {
      if (file.status === "error") return;
      simulateUpload(i);
    });
  };

  const simulateUpload = (index) => {
    const interval = setInterval(() => {
      setFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        if (newFiles[index].progress < 100) {
          newFiles[index].progress += 10;
        } else {
          newFiles[index].status = "completed";
          clearInterval(interval);
        }
        return newFiles;
      });
    }, 300);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleScanDocument = async () => {
    setLoading(true);
    setResult("");

    try {
      if (activeTab === "camera" && capturedImage) {
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        const file = new File([blob], "captured_image.png", { type: blob.type });

        const resultText = await processPrescriptionFile(file);
        setResult(resultText);
      } else {
        const completedFiles = files.filter((f) => f.status === "completed");
        if (completedFiles.length === 0) {
          alert("Please upload and complete at least one file.");
          return;
        }

        const responses = await Promise.all(
          completedFiles.map((file) => processPrescriptionFile(file.file))
        );
        setResult(responses.join("\n"));
      }
    } catch (error) {
      console.error(error);
      setResult("Error processing the document.");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  const formatBoldText = (line) => {
    // Replace all instances of **text** with a span
    const parts = line.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={i} className="text-black font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };


  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <Scan />
          <h1>Document Scanner</h1>
        </div>
      </div>

      <div className="scanner-container">
        <div className="scanner-content">
          <h2>Scan Medical Documents</h2>
          <p>Upload a lab report, prescription, or medical bill to analyze it with AI</p>

          <div className="upload-tabs">
            <button
              className={`upload-tab ${activeTab === "upload" ? "active" : ""}`}
              onClick={() => handleTabChange("upload")}
            >
              Upload File
            </button>
            {/* <button
              className={`upload-tab ${activeTab === "camera" ? "active" : ""}`}
              onClick={() => handleTabChange("camera")}
            >
              Camera
            </button> */}
          </div>

          {activeTab === "upload" && (
            <div
              className={`dropzone ${isHovering ? "hovering" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsHovering(true);
              }}
              onDragLeave={() => setIsHovering(false)}
              onDrop={handleDrop}
            >
              <div className="dropzone-content">
                <FaCloudUploadAlt className="upload-icon" />
                <h3>Drag and drop your file here</h3>
                <p>Support for PDF, JPG, PNG files up to 5MB</p>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  id="fileInput"
                  hidden
                  onChange={handleFileChange}
                />
                <label htmlFor="fileInput" className="select-file-button">
                  Browse Files
                </label>
              </div>
              {files.length > 0 && (
                <div className="uploaded-files">
                  {files.map((file, i) => (
                    <div className={`file-item ${file.status}`} key={i}>
                      <div className="file-info">
                        <FaFileAlt />
                        <div>
                          <div>{file.name}</div>
                          <div>{formatFileSize(file.size)}</div>
                        </div>
                      </div>
                      <div className="file-status">
                        {file.status === "uploading" && (
                          <>
                            <div className="flex justify-center items-center gap-2 mt-2">
                              <div className="progress-bar">
                                <div className="progress" style={{ width: `${file.progress}%` }} />
                              </div>
                              <ClipLoader size={15} color="#6366f1" />
                            </div>

                          </>
                        )}
                        {/* {file.status === "completed" && (
                          <div className="status-indicator completed">
                            <FaCheckCircle /> Completed
                          </div>
                        )} */}
                        {file.status === "error" && (
                          <div className="status-indicator error text-red-500 flex items-center gap-2 mt-1">
                            <FaExclamationTriangle className="text-red-500" />
                            <span>File too large</span>
                          </div>
                        )}
                        <button className="remove-btn" onClick={() => removeFile(i)}>
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "camera" && (
            <div className="camera-container">
              {!capturedImage ? (
                <>
                  <video ref={videoRef} autoPlay playsInline muted className="camera-preview" />
                  {cameraActive && (
                    <button className="capture-button" onClick={captureImage}>
                      <Camera />
                    </button>
                  )}
                </>
              ) : (
                <div className="captured-image-container">
                  <img src={capturedImage} alt="Captured" className="captured-image" />
                  <button onClick={resetCapture} className="reset-capture-button">
                    <X />
                  </button>
                </div>
              )}
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
          )}

          {/* <div className="scanner-actions">
            <button
              className="reset-button"
              onClick={() => {
                resetCapture();
                if (activeTab === "camera") startCamera();
              }}
            >
              Reset
            </button>
            <button
              className="scan-button"
              onClick={handleScanDocument}
              disabled={activeTab === "camera" && !capturedImage}
            >
              <Scan className="button-icon" />
              {loading ? "Analyzing..." : "Scan Document"}
            </button>
          </div> */}

          {loading ? (
            <div className="w-full flex justify-center mt-6">
              <div className="result-container flex items-center gap-3">
                <ClipLoader size={24} color="#951C25" />
                <span className="text-[#951C25] font-medium text-lg">Analyzing document...</span>
              </div>
            </div>

          ) : result && (
            <div className="result-container bg-white p-6 rounded-lg shadow-md border mt-6">
              <h3 className="text-xl font-bold text-[#951C25] mb-4">Extracted Details</h3>
              <div className="result-content text-left space-y-2">
                {result.split("\n").map((line, index) => {
                  if (line.startsWith("### "))
                    return (
                      <h3 key={index} className="text-xl font-semibold text-[#951C25] mb-2">
                        {line.replace("### ", "")}
                      </h3>
                    );

                  if (line.startsWith("#### "))
                    return (
                      <h4 key={index} className="text-lg font-semibold text-gray-700 mb-1">
                        {line.replace("#### ", "")}
                      </h4>
                    );

                  if (line.startsWith("- **")) {
                    const parts = line.match(/- \*\*(.*?)\*\*: (.*)/);
                    return parts ? (
                      <p key={index} className="mb-1">
                        <span className="font-semibold text-black">{parts[1]}:</span>{" "}
                        <span className="text-gray-700">{parts[2]}</span>
                      </p>
                    ) : (
                      <p key={index} className="text-gray-700 mb-1">{formatBoldText(line)}</p>
                    );
                  }

                  if (line.startsWith("- "))
                    return (
                      <li key={index} className="ml-5 list-disc text-gray-700">
                        {formatBoldText(line.replace("- ", ""))}
                      </li>
                    );

                  return (
                    <p key={index} className="text-gray-700 mb-1">
                      {formatBoldText(line)}
                    </p>
                  );
                })}

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
